/**
 * Canonical public SEO origin for the Product Experience Platform (PEP).
 *
 * Production truth is always www.itaas-suite.com.
 * Preview / deployment URLs must never become production SEO artifacts.
 */
export const PEP_CANONICAL_PRODUCTION_HOST = "www.itaas-suite.com";
export const PEP_CANONICAL_PRODUCTION_ORIGIN = "https://www.itaas-suite.com";
/** Hosts that must never appear in production SEO outputs. */
export const PEP_FORBIDDEN_SEO_HOSTS = [
    "itas.com",
    "www.itas.com",
    "localhost",
    "127.0.0.1",
    "0.0.0.0",
];
/** Explicit non-production hosts allowed only when PEP_SEO_ENV=staging. */
export const PEP_ALLOWED_STAGING_SEO_HOSTS = ["pep.staging.itaas-rdc.site"];
export class PepSeoValidationError extends Error {
    code;
    constructor(code, message) {
        super(`${code}: ${message}`);
        this.name = "PepSeoValidationError";
        this.code = code;
    }
}
export function normalizePepOrigin(url) {
    return url.trim().replace(/\/+$/, "");
}
export function getPepOriginHost(origin) {
    return new URL(origin).hostname.toLowerCase();
}
export function isForbiddenSeoHost(host) {
    const h = host.toLowerCase();
    if (PEP_FORBIDDEN_SEO_HOSTS.includes(h))
        return true;
    if (h.endsWith(".itas.com"))
        return true;
    if (h === "itas.com")
        return true;
    return false;
}
export function isAllowedStagingSeoHost(host) {
    return PEP_ALLOWED_STAGING_SEO_HOSTS.includes(host.toLowerCase());
}
export function resolvePepSeoEnv(env = process.env) {
    const raw = (env.PEP_SEO_ENV ?? "production").trim().toLowerCase();
    return raw === "staging" ? "staging" : "production";
}
/**
 * Resolve the base URL used for sitemap / robots / route-meta generation.
 *
 * - production (default): always https://www.itaas-suite.com
 *   (optional PEP_SITE_URL / PEP_SEO_BASE_URL must match the canonical host)
 * - staging: requires PEP_SITE_URL (or PEP_SEO_BASE_URL) on an allowlisted staging host
 *
 * Never inherits Vercel/preview deployment hostnames into production artifacts.
 */
export function resolvePepSeoBaseUrl(env = process.env) {
    const seoEnv = resolvePepSeoEnv(env);
    const raw = (env.PEP_SEO_BASE_URL ?? env.PEP_SITE_URL ?? "").trim();
    if (seoEnv === "staging") {
        if (!raw) {
            throw new PepSeoValidationError("INVALID_CANONICAL_HOST", "PEP_SITE_URL or PEP_SEO_BASE_URL is required when PEP_SEO_ENV=staging");
        }
        return assertValidPepSeoBaseUrl(raw, "staging");
    }
    if (!raw) {
        return PEP_CANONICAL_PRODUCTION_ORIGIN;
    }
    return assertValidPepSeoBaseUrl(raw, "production");
}
export function assertValidPepSeoBaseUrl(raw, seoEnv = "production") {
    let origin;
    try {
        origin = normalizePepOrigin(raw);
        // Ensure absolute URL
        // eslint-disable-next-line no-new
        new URL(origin);
    }
    catch {
        throw new PepSeoValidationError("INVALID_CANONICAL_HOST", `Invalid origin URL: ${raw}`);
    }
    const url = new URL(origin);
    const host = url.hostname.toLowerCase();
    if (isForbiddenSeoHost(host)) {
        throw new PepSeoValidationError("FORBIDDEN_DOMAIN", `Host "${host}" is forbidden for PEP SEO`);
    }
    if (url.protocol !== "https:") {
        throw new PepSeoValidationError("HTTP_PRODUCTION_URL", `SEO base URL must use HTTPS (got ${url.protocol}//${host})`);
    }
    if (host.includes("localhost") || host === "127.0.0.1") {
        throw new PepSeoValidationError("LOCALHOST_PRODUCTION_URL", `Localhost host "${host}" is not allowed for SEO artifacts`);
    }
    if (seoEnv === "production") {
        if (host !== PEP_CANONICAL_PRODUCTION_HOST) {
            throw new PepSeoValidationError("INVALID_CANONICAL_HOST", `Expected host ${PEP_CANONICAL_PRODUCTION_HOST}, got ${host}`);
        }
    }
    else if (!isAllowedStagingSeoHost(host)) {
        throw new PepSeoValidationError("INVALID_CANONICAL_HOST", `Staging host "${host}" is not allowlisted`);
    }
    return normalizePepOrigin(url.origin);
}
/** Runtime public site URL for the SPA (Vite). Never defaults to itas.com. */
export function resolvePepRuntimeSiteUrl(viteSiteUrl) {
    const raw = (viteSiteUrl ?? "").trim();
    if (!raw)
        return PEP_CANONICAL_PRODUCTION_ORIGIN;
    const origin = normalizePepOrigin(raw);
    const host = getPepOriginHost(origin);
    if (isForbiddenSeoHost(host)) {
        return PEP_CANONICAL_PRODUCTION_ORIGIN;
    }
    return origin;
}
